import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X, MapPin, Star, Shuffle, Users, UtensilsCrossed, ClipboardList, ArrowLeft, Smartphone } from 'lucide-react';
import { Link } from 'react-router-dom';
import './LearnMore.css';

const features = [
  {
    id: 'dashboard',
    title: 'Your Dining Command Center',
    description: 'See your most recent visits, nearest restaurants, and a scrollable photo montage of your dining adventures — all at a glance.',
    icon: UtensilsCrossed,
    screenshot: '/screenshots/dashboard.png',
    color: '#10b981',
  },
  {
    id: 'myrestaurants',
    title: 'Your Complete Restaurant Database',
    description: 'Track every restaurant with cuisine type, price range, your personal rating, Google scores, favorite dishes, and staff notes. Sort and filter to find exactly what you want.',
    icon: Star,
    screenshot: '/screenshots/myrestaurants.png',
    color: '#f59e0b',
  },
  {
    id: 'details',
    title: 'Deep Restaurant Details',
    description: 'Tap any restaurant for the full story — visit history, total spending, guest lists, Google ratings, amenities, and even text-to-speech to read it all aloud.',
    icon: MapPin,
    screenshot: '/screenshots/details.png',
    color: '#6366f1',
  },
  {
    id: 'ondeck',
    title: 'On Deck — Your Dining Wishlist',
    description: "Queue up restaurants you want to try next. See Google ratings, distance, phone numbers, and websites. When you're ready, check in with one click.",
    icon: ClipboardList,
    screenshot: '/screenshots/ondeck.png',
    color: '#ec4899',
  },
  {
    id: 'random',
    title: "Can't Decide? Random Pick!",
    description: "Hit the Random Pick button and let Dine-A-Saurus surprise you. It's like spinning a wheel of your favorite restaurants. Perfect for those 'I don't know, where do YOU want to eat?' moments.",
    icon: Shuffle,
    screenshot: '/screenshots/random.png',
    color: '#f97316',
  },
  {
    id: 'shared',
    title: 'Family Groups & Sharing',
    description: 'Create a family group and share your restaurant discoveries with everyone. See what your family has been eating, where they went, and discover new favorites together.',
    icon: Users,
    screenshot: '/screenshots/shared.png',
    color: '#14b8a6',
  },
];

const mobileScreens = [
  { id: 'mobile-dashboard', title: 'Dashboard', screenshot: '/screenshots/mobile-dashboard.png' },
  { id: 'mobile-biometric', title: 'Fingerprint Login', screenshot: '/screenshots/mobile-biometric.png' },
  { id: 'mobile-ondeck', title: 'On Deck', screenshot: '/screenshots/mobile-ondeck.png' },
  { id: 'mobile-map', title: 'Restaurant Map', screenshot: '/screenshots/mobile-map.png' },
  { id: 'mobile-details', title: 'Details', screenshot: '/screenshots/mobile-details.png' },
];

export default function LearnMore() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxSource, setLightboxSource] = useState('features'); // 'features' or 'mobile'
  const [visibleCards, setVisibleCards] = useState(new Set());

  const currentSlides = lightboxSource === 'mobile' ? mobileScreens : features;

  const openLightbox = (index, source = 'features') => {
    setLightboxIndex(index);
    setLightboxSource(source);
    setLightboxOpen(true);
  };

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const nextSlide = useCallback(() => {
    setLightboxIndex((prev) => (prev + 1) % (lightboxSource === 'mobile' ? mobileScreens.length : features.length));
  }, [lightboxSource]);

  const prevSlide = useCallback(() => {
    setLightboxIndex((prev) => (prev - 1 + (lightboxSource === 'mobile' ? mobileScreens.length : features.length)) % (lightboxSource === 'mobile' ? mobileScreens.length : features.length));
  }, [lightboxSource]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextSlide();
      if (e.key === 'ArrowLeft') prevSlide();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxOpen, closeLightbox, nextSlide, prevSlide]);

  // Intersection observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleCards((prev) => new Set([...prev, entry.target.dataset.id]));
          }
        });
      },
      { threshold: 0.15 }
    );
    document.querySelectorAll('.lm-feature-card').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="lm-page" data-testid="learn-more-page">
      {/* Top Nav Bar */}
      <nav className="lm-topnav">
        <Link to="/" className="lm-topnav-link" data-testid="learn-more-back-btn">
          <ArrowLeft size={18} />
          <span>Back to Login</span>
        </Link>
        <Link to="/request-access" className="lm-topnav-cta" data-testid="learn-more-topnav-request">
          Request Access
        </Link>
      </nav>

      {/* Hero */}
      <section className="lm-hero">
        <div className="lm-hero-bg" />
        <div className="lm-hero-content">
          <h1 className="lm-hero-title">
            Dine-A-Saurus
          </h1>
          <p className="lm-hero-subtitle">
            Track every restaurant, rate every dish, share with family — and never wonder "where should we eat?" again.
          </p>
        </div>
      </section>

      {/* Mobile Section */}
      <section className="lm-mobile" data-testid="mobile-section">
        <div className="lm-mobile-header">
          <div className="lm-mobile-icon">
            <Smartphone size={24} />
          </div>
          <h2 className="lm-section-title">Built for Your Phone and Lifestyle!</h2>
          <p className="lm-section-subtitle">
            Every feature works beautifully on mobile. Track restaurants, check ratings, and find your next meal — right from your pocket.
          </p>
        </div>
        <div className="lm-mobile-phones">
          {mobileScreens.map((screen, index) => (
            <div
              key={screen.id}
              className="lm-phone-frame"
              onClick={() => openLightbox(index, 'mobile')}
              data-testid={`mobile-screenshot-${screen.id}`}
            >
              <div className="lm-phone-notch" />
              <div className="lm-phone-screen">
                <img src={screen.screenshot} alt={screen.title} loading="lazy" />
              </div>
              <p className="lm-phone-label">{screen.title}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="lm-features">
        <h2 className="lm-section-title">Everything You Need</h2>
        <p className="lm-section-subtitle">Real screenshots from the app — what you see is what you get.</p>
        <div className="lm-features-grid">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            const isVisible = visibleCards.has(feature.id);
            return (
              <div
                key={feature.id}
                className={`lm-feature-card ${isVisible ? 'lm-visible' : ''}`}
                data-id={feature.id}
                data-testid={`feature-card-${feature.id}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="lm-card-header">
                  <div className="lm-card-icon" style={{ backgroundColor: `${feature.color}15`, color: feature.color }}>
                    <Icon size={22} />
                  </div>
                  <h3 className="lm-card-title">{feature.title}</h3>
                </div>
                <p className="lm-card-desc">{feature.description}</p>
                <div
                  className="lm-card-screenshot"
                  onClick={() => openLightbox(index)}
                  data-testid={`screenshot-${feature.id}`}
                >
                  <img src={feature.screenshot} alt={feature.title} loading="lazy" />
                  <div className="lm-card-screenshot-overlay">
                    <span>Click to enlarge</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="lm-cta">
        <h2 className="lm-cta-title">Ready to Track Your Dining Adventures?</h2>
        <p className="lm-cta-text">Join now and never forget a great meal again.</p>
        <div className="lm-cta-links">
          <div className="lm-cta-request" data-testid="learn-more-request-access">
            Not signed up? <Link to="/request-access">Request access here</Link>
          </div>
          <Link to="/request-access" className="lm-cta-button" data-testid="learn-more-get-started-btn">
            Get Started
          </Link>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <div className="lm-lightbox" data-testid="learn-more-lightbox" onClick={closeLightbox}>
          <div className="lm-lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <button className="lm-lightbox-close" onClick={closeLightbox} data-testid="lightbox-close-btn">
              <X size={24} />
            </button>
            <button className="lm-lightbox-nav lm-lightbox-prev" onClick={prevSlide} data-testid="lightbox-prev-btn">
              <ChevronLeft size={28} />
            </button>
            <button className="lm-lightbox-nav lm-lightbox-next" onClick={nextSlide} data-testid="lightbox-next-btn">
              <ChevronRight size={28} />
            </button>
            <div className="lm-lightbox-body">
              <div className="lm-lightbox-img-wrap">
                <img
                  src={currentSlides[lightboxIndex].screenshot}
                  alt={currentSlides[lightboxIndex].title}
                  className={`lm-lightbox-img ${lightboxSource === 'mobile' ? 'lm-lightbox-img-mobile' : ''}`}
                />
              </div>
              <div className="lm-lightbox-caption">
                <h3>{currentSlides[lightboxIndex].title}</h3>
                {currentSlides[lightboxIndex].description && (
                  <p>{currentSlides[lightboxIndex].description}</p>
                )}
              </div>
              <div className="lm-lightbox-dots">
                {currentSlides.map((_, i) => (
                  <button
                    key={i}
                    className={`lm-dot ${i === lightboxIndex ? 'lm-dot-active' : ''}`}
                    onClick={() => setLightboxIndex(i)}
                    data-testid={`lightbox-dot-${i}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
